import { useEffect, useState } from "react";
import { lumiFetch } from "./lumiApi";
import { toast } from "./lumiToast";

export default function LumiSettings({ onClose }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await lumiFetch("/settings/email");
      setS(r);
    } catch (e) { toast.error(e?.data?.detail || "Could not load settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pw) return toast.error("Enter the mailbox password");
    setSaving(true);
    try {
      await lumiFetch("/settings/email", { method: "POST", body: JSON.stringify({ password: pw }) });
      toast.success("Settings saved 🔐");
      setPw("");
      load();
    } catch (e) { toast.error(e?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await lumiFetch("/settings/email/test", { method: "POST" });
      setTestResult(r);
      if (r.imap === "ok" && r.smtp === "ok") toast.success("Both IMAP and SMTP connected ✅");
      else toast.error("One or both connections failed — see details");
    } catch (e) { toast.error(e?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="p-6 text-white/50 text-sm">Loading…</div>;

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto w-full" data-testid="lumi-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Settings · Mailbox</h2>
          <p className="text-xs text-white/50 mt-1">For reading replies and sending replies as <strong>hello@poodlesandfriends.uk</strong>.</p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs text-white/50 mb-1">IMAP server</div>
            <div className="font-mono text-sm">{s?.imap_host || "mail.livemail.co.uk"}</div>
            <div className="text-xs text-white/40">Port {s?.imap_port || 993} · SSL</div>
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1">SMTP server</div>
            <div className="font-mono text-sm">{s?.smtp_host || "smtp.livemail.co.uk"}</div>
            <div className="text-xs text-white/40">Port {s?.smtp_port || 465} · SSL</div>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-white/50 mb-1">Mailbox</div>
            <div className="font-mono text-sm">{s?.username || "hello@poodlesandfriends.uk"}</div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-4">
          {s?.configured ? (
            <div className="text-xs text-emerald-300/80 mb-2">
              ● Password configured (ends in <code className="font-mono">…{s.password_last4}</code>) · last updated {s.updated_at ? new Date(s.updated_at).toLocaleString() : "?"}
            </div>
          ) : (
            <div className="text-xs text-amber-300/80 mb-2">⚠ No password set yet — enter it below.</div>
          )}
          <label className="text-xs text-white/50">Mailbox password</label>
          <div className="flex gap-2 mt-1">
            <input
              data-testid="lumi-mailbox-password"
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={s?.configured ? "Enter to replace existing" : "Type password"}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <button onClick={() => setShowPw(!showPw)} className="text-xs px-3 py-2 border border-white/20 rounded-lg hover:bg-white/10">{showPw ? "Hide" : "Show"}</button>
            <button data-testid="lumi-save-mailbox" onClick={save} disabled={saving || !pw} className="text-sm px-4 py-2 bg-white text-black rounded-lg disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
          </div>
          <p className="text-xs text-white/40 mt-2">Stored encrypted in the database. Used only by the backend to fetch and send mail.</p>
        </div>

        {s?.configured && (
          <div className="border-t border-white/10 pt-4">
            <button data-testid="lumi-test-mailbox" onClick={test} disabled={testing} className="text-sm px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 disabled:opacity-50">
              {testing ? "Testing…" : "🔌 Test connection"}
            </button>
            {testResult && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className={`p-2 rounded ${testResult.imap === "ok" ? "bg-emerald-300/10 text-emerald-200" : "bg-rose-300/10 text-rose-200"}`}>
                  IMAP: {testResult.imap}
                </div>
                <div className={`p-2 rounded ${testResult.smtp === "ok" ? "bg-emerald-300/10 text-emerald-200" : "bg-rose-300/10 text-rose-200"}`}>
                  SMTP: {testResult.smtp}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
